import { NavLink, useNavigate } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";

/** Small pieces shared by the Outbound pages (trustedge prospecting/). */

export const TABS = [
    { href: "/outbound", label: "Overview", end: true },
    { href: "/outbound/prospects", label: "Prospects" },
    { href: "/outbound/sequences", label: "Sequences" },
    { href: "/outbound/outbox", label: "Outbox" },
    { href: "/outbound/replies", label: "Replies" },
];

export function OutboundHeader({ icon: Icon, title, subtitle, children }) {
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    {Icon && <Icon className="h-5 w-5 text-primary" />}
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
                        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 max-w-3xl">{subtitle}</p>}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">{children}</div>
            </div>
            <nav className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
                {TABS.map(t => (
                    <NavLink key={t.href} to={t.href} end={t.end}
                        className={({ isActive }) => cn(
                            "px-3 py-1.5 text-xs font-medium border-b-2 -mb-px whitespace-nowrap",
                            isActive ? "border-primary text-primary"
                                : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200")}>
                        {t.label}
                    </NavLink>
                ))}
            </nav>
        </div>
    );
}

export function Btn({ children, variant = "default", className, ...rest }) {
    return (
        <button type="button" {...rest}
            className={cn("h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed",
                variant === "primary" && "bg-primary text-white hover:opacity-90",
                variant === "danger" && "border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20",
                variant === "default" && "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40",
                variant === "ghost" && "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200",
                className)}>
            {children}
        </button>
    );
}

export function Toggle({ on, onClick, disabled }) {
    return (
        <button type="button" disabled={disabled} onClick={onClick} aria-pressed={on}
            className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
                on ? "bg-primary" : "bg-gray-300 dark:bg-gray-600", disabled && "opacity-50 cursor-not-allowed")}>
            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                on ? "translate-x-6" : "translate-x-1")} />
        </button>
    );
}

const TONES = {
    gray: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
    green: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    red: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    amber: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    blue: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
    brand: "bg-primary/10 text-primary",
};

export function Pill({ tone = "gray", children, title }) {
    return <span title={title} className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap", TONES[tone])}>{children}</span>;
}

export const PROSPECT_TONE = {
    new: "gray", profiled: "blue", no_email: "amber", existing_lead: "gray", enrolled: "brand",
    replied: "blue", converted: "green", not_now: "amber", not_interested: "gray",
    unsubscribed: "red", bounced: "red", excluded: "gray",
};

export const VERIFY_TONE = { verified: "green", risky: "amber", invalid: "red", unknown: "gray" };

export const INTENT_TONE = {
    interested: "green", meeting: "green", question: "blue", not_now: "amber",
    not_interested: "gray", unsubscribe: "red", out_of_office: "gray", bounce: "red", other: "gray", "": "amber",
};

export const OUTBOX_TONE = {
    draft: "amber", approved: "blue", sending: "blue", sent: "green", rejected: "gray",
    failed: "red", blocked: "red", cancelled: "gray",
};

export function Stat({ label, value, hint }) {
    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-xl font-semibold tabular-nums text-gray-900 dark:text-white mt-0.5">{value ?? "–"}</p>
            {hint && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{hint}</p>}
        </div>
    );
}

export function Section({ title, subtitle, right, children, className }) {
    return (
        <section className={cn("rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3", className)}>
            {(title || right) && (
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="text-xs">
                        {title && <p className="font-semibold text-gray-900 dark:text-white text-sm">{title}</p>}
                        {subtitle && <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-3xl">{subtitle}</p>}
                    </div>
                    {right}
                </div>
            )}
            {children}
        </section>
    );
}

export const inputCls = "w-full text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-gray-900 dark:text-gray-100";

/** Open a CRM lead the same way the lead list does. */
export function useOpenLead() {
    const navigate = useNavigate();
    return (id) => navigate(`/customers/customerdetails?id=${id}`, { state: { formEdit: true, row: { id } } });
}
